import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const TARGET_DATE = new Date('2026-11-10T00:00:00');

const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <div
      className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-2xl flex items-center justify-center"
      style={{
        background: 'rgba(26, 37, 86, 0.6)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(255, 107, 53, 0.25)',
        boxShadow: '0 0 40px rgba(255, 107, 53, 0.12)'
      }}
    >
      <span
        className="text-3xl sm:text-5xl md:text-6xl font-bold text-white tabular-nums"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="mt-3 text-xs sm:text-sm uppercase tracking-[0.2em] text-slate-400 font-medium">{label}</span>
  </div>
);

const TarranoxCountdown = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = Math.max(0, TARGET_DATE - now);
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437] flex flex-col">
      <header className="bg-[#0B1437]/80 backdrop-blur-xl px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/lhvv6y36_Menelaus%20Holding.png"
              alt="Menelaus Holding"
              className="w-10 h-10"
            />
            <span className="text-white font-semibold text-sm hidden sm:block">Menelaus Holding</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
        <div className="text-center max-w-3xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#FF6B35] font-bold mb-6 sm:mb-8"
          >
            The Mission
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight"
            data-testid="tarranox-title"
          >
            Create the First Fully Functional{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
              Tarranox Buggy
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-slate-400 text-sm sm:text-base md:text-lg mb-14 sm:mb-20"
          >
            November 10, 2026
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex justify-center gap-3 sm:gap-5 md:gap-8"
            data-testid="countdown-timer"
          >
            <CountdownUnit value={timeLeft.days} label="Days" />
            <CountdownUnit value={timeLeft.hours} label="Hours" />
            <CountdownUnit value={timeLeft.minutes} label="Minutes" />
            <CountdownUnit value={timeLeft.seconds} label="Seconds" />
          </motion.div>
        </div>
      </main>

      <footer className="py-6 sm:py-8">
        <p className="text-center text-slate-500 text-xs sm:text-sm">
          &copy; 2026 Menelaus Holding. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default TarranoxCountdown;
